import React, { useState } from 'react';

interface FunnelData {
  label: string;
  value: number;
  color: string;
}

export interface FunnelOptions {
  showLegend?: boolean;
}

interface Props {
  data: FunnelData[];
  options?: FunnelOptions
}

export const FunnelChart: React.FC<Props> = ({ data, options }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipY, setTooltipY] = useState(0);

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const width = 700;
  const padding = 60;
  const rightPadding = 60;
  const funnelWidth = width - padding - rightPadding;
  const funnelCenterX = padding + funnelWidth / 2;

  // Hauteur dynamique basée sur le nombre d'étapes
  const segmentHeight = 100;
  const height = padding * 2 + (data.length * segmentHeight);

  const handleMouseMove = (
    e: React.MouseEvent<SVGElement, MouseEvent>,
    index: number
  ) => {
    const svg = e.currentTarget.closest('svg') as SVGSVGElement | null;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    setTooltipY(e.clientY - rect.top);
    setHoveredIndex(index);
  };

  return (
    <>
      <div className="d-flex justify-content-center position-relative">
        <svg
          width={width}
          height={height}
          style={{ overflow: 'visible' }}
        >
          {data.map((item, index) => {
            const y = padding + (index * segmentHeight);

            // Si la valeur est 0, afficher juste le label/valeur sans segment
            if (item.value === 0) {
              return (
                <g key={index}>
                  {/* Label centré */}
                  <text
                    x={funnelCenterX}
                    y={y + segmentHeight / 2 - 10}
                    textAnchor="middle"
                    fill={item.color}
                    fontSize="16"
                    fontWeight="bold"
                  >
                    {item.label}
                  </text>

                  {/* Valeur 0 centrée */}
                  <text
                    x={funnelCenterX}
                    y={y + segmentHeight / 2 + 15}
                    textAnchor="middle"
                    fill="#6c757d"
                    fontSize="20"
                    fontWeight="bold"
                  >
                    0
                  </text>
                </g>
              );
            }

            // Calculer les largeurs pour les valeurs non-zéro
            const topWidth = (item.value / maxValue) * funnelWidth;

            // Trouver la prochaine valeur non-zéro
            let nextValue = 0;
            for (let i = index + 1; i < data.length; i++) {
              if (data[i].value > 0) {
                nextValue = data[i].value;
                break;
              }
            }

            // Si pas de valeur suivante, réduire à 30% de la largeur actuelle
            const bottomWidth = nextValue > 0
              ? (nextValue / maxValue) * funnelWidth
              : topWidth * 0.3;

            const topLeft = padding + (funnelWidth - topWidth) / 2;
            const topRight = padding + (funnelWidth + topWidth) / 2;
            const bottomLeft = padding + (funnelWidth - bottomWidth) / 2;
            const bottomRight = padding + (funnelWidth + bottomWidth) / 2;

            const points = `
              ${topLeft},${y}
              ${topRight},${y}
              ${bottomRight},${y + segmentHeight}
              ${bottomLeft},${y + segmentHeight}
            `;

            // Vérifier si le texte rentre (largeur min 150px)
            const textFitsInFunnel = topWidth > 150;

            return (
              <g key={index}>
                {/* Segment du funnel */}
                <polygon
                  points={points}
                  fill={item.color}
                  stroke="white"
                  strokeWidth="3"
                  style={{
                    transition: 'opacity 0.3s',
                    cursor: 'pointer',
                    opacity: hoveredIndex === index ? 0.8 : 1
                  }}
                  onMouseEnter={(e) => handleMouseMove(e, index)}
                  onMouseMove={(e) => handleMouseMove(e, index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />

                {/* Label et valeur - seulement si ça rentre */}
                {textFitsInFunnel && (
                  <>
                    <text
                      x={funnelCenterX}
                      y={y + segmentHeight / 2 - 10}
                      textAnchor="middle"
                      fill="white"
                      fontSize="16"
                      fontWeight="bold"
                      pointerEvents="none"
                    >
                      {item.label}
                    </text>

                    <text
                      x={funnelCenterX}
                      y={y + segmentHeight / 2 + 15}
                      textAnchor="middle"
                      fill="white"
                      fontSize="22"
                      fontWeight="bold"
                      pointerEvents="none"
                    >
                      {item.value}
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip centré en X par rapport au funnel */}
        {hoveredIndex !== null && data[hoveredIndex].value > 0 && (
          <div
            className="position-absolute bg-dark text-white px-3 py-2 rounded shadow"
            style={{
              left: funnelCenterX,
              top: tooltipY - 90,
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
              zIndex: 1000,
              whiteSpace: 'nowrap',
              fontSize: '13px',
              lineHeight: '1.6'
            }}
          >
            <div className="fw-bold mb-1" style={{ fontSize: '14px' }}>
              {data[hoveredIndex].label}
            </div>
            <div>Valeur: <span className="fw-semibold">{data[hoveredIndex].value}</span></div>
            <div className="text-info">
              {((data[hoveredIndex].value / data[0].value) * 100).toFixed(1)}% du total
            </div>
            {hoveredIndex > 0 && (() => {
              let prevValue = 0;
              for (let i = hoveredIndex - 1; i >= 0; i--) {
                if (data[i].value > 0) {
                  prevValue = data[i].value;
                  break;
                }
              }
              const conversionRate = prevValue > 0
                ? ((data[hoveredIndex].value / prevValue) * 100).toFixed(1)
                : '100.0';
              const dropOff = prevValue - data[hoveredIndex].value;

              return (
                <>
                  <div className="text-success">
                    {conversionRate}% de conversion
                  </div>
                  {dropOff > 0 && (
                    <div className="text-danger">
                      -{dropOff} perdus
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>

      { options?.showLegend && (
        <div className="row mb-4 g-3 justify-content-center">
          {data.map((item, index) => {
            let dropOff = 0;
            if (index > 0) {
              for (let i = index - 1; i >= 0; i--) {
                if (data[i].value > 0) {
                  dropOff = data[i].value - item.value;
                  break;
                }
              }
            }

            return (
              <div key={index} className="col-6 col-md-3">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded"
                    style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: item.color,
                      flexShrink: 0
                    }}
                  />
                  <span className="text-dark small">
                    {item.label}: <span className="fw-semibold">{item.value}</span>
                    {dropOff > 0 && (
                      <span className="text-danger ms-1" style={{ fontSize: '0.7rem' }}>
                        (-{dropOff})
                      </span>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
